import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import usePageMeta from "../hooks/usePageMeta";

const CATEGORIES = [
  { id: "all", name: "All Products" },
  { id: "essential-oils", name: "Essential Oils" },
  { id: "diy-supplies", name: "DIY Supplies" },
  { id: "garden-gifts", name: "Home, Garden & Gifts" },
];

const SORT_OPTIONS = [
  { id: "featured", name: "Featured" },
  { id: "price-asc", name: "Price: Low to High" },
  { id: "price-desc", name: "Price: High to Low" },
  { id: "name", name: "Name A-Z" },
];

const CATEGORY_META = {
  "all": {
    title: "Shop All — Essential Oils, DIY Supplies & Garden Gifts | DC Gift World Too",
    description: "Browse 40+ hand-curated products: pure essential oils, candle & soap making supplies, and storybook garden gifts. Free shipping over $75.",
  },
  "essential-oils": {
    title: "Essential Oils — Pure, GC/MS Tested, Simply Earth Ambassador | DC Gift World Too",
    description: "Shop pure essential oils — lavender, peppermint, eucalyptus, lemon, and more. Simply Earth Ambassador · 100% GC/MS lab-tested · free shipping over $75.",
  },
  "diy-supplies": {
    title: "DIY Candle & Soap Making Supplies — Kits, Molds & Bottles | DC Gift World Too",
    description: "Everything you need to make candles, soaps, and roller blends at home: silicone molds, amber roller bottles, perfumer's alcohol, and starter kits. Free shipping over $75.",
  },
  "garden-gifts": {
    title: "Storybook Garden Gifts — Gnomes, Fairies, Statues & Planters | DC Gift World Too",
    description: "Whimsical garden statues, solar gnomes, fairy figurines, birdhouses, and cozy home accents to bring storybook magic to your porch, patio, or garden.",
  },
};

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");

  const category = searchParams.get("category") || "all";
  const q = searchParams.get("q") || "";

  const meta = CATEGORY_META[category] || CATEGORY_META.all;
  usePageMeta({
    title: q ? `"${q}" — Shop Results | DC Gift World Too` : meta.title,
    description: meta.description,
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "all") params.category = category;
    if (q) params.q = q;
    api
      .get("/products", { params })
      .then((res) => setProducts(res.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return list;
  }, [products, sort]);

  const setCategory = (id) => {
    const np = new URLSearchParams(searchParams);
    if (id === "all") np.delete("category");
    else np.set("category", id);
    setSearchParams(np);
  };

  const currentCat = CATEGORIES.find((c) => c.id === category) || CATEGORIES[0];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="shop-page">
      <div className="mb-12">
        <p className="eyebrow mb-3">Shop</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-[var(--ink-forest)] tracking-tight">
          {q ? <>Searching for <span className="italic text-[var(--terracotta)]">&ldquo;{q}&rdquo;</span></> : currentCat.name}
        </h1>
        {!q && (
          <p className="mt-4 text-[var(--muted-fg)] max-w-xl">
            {currentCat.id === "all" && "Everything we carry, gathered in one place."}
            {currentCat.id === "essential-oils" && "Pure singles and signature blends — sourced from farms where each plant grows in its native climate, then quality-tested and bottled by hand."}
            {currentCat.id === "diy-supplies" && "Tools, molds, and bottles for makers."}
            {currentCat.id === "garden-gifts" && "Statues, planters, birdhouses, home accents and one-of-a-kind gift ideas."}
          </p>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-10">
        {/* Sidebar */}
        <aside className="lg:w-60 shrink-0">
          <div className="lg:sticky lg:top-28 space-y-8">
            <div>
              <p className="eyebrow mb-4 flex items-center gap-2">
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Categories
              </p>
              <ul className="space-y-1.5">
                {CATEGORIES.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setCategory(c.id)}
                      className={`text-left text-sm py-1.5 transition-colors ${
                        category === c.id
                          ? "text-[var(--terracotta)] font-semibold"
                          : "text-[var(--ink-forest)] hover:text-[var(--terracotta)]"
                      }`}
                      data-testid={`filter-category-${c.id}`}
                    >
                      {c.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="eyebrow mb-4">Sort</p>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="w-full bg-[var(--bg-surface)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--terracotta)]"
                data-testid="sort-select"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square bg-[var(--muted)] rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif text-2xl text-[var(--ink-forest)]">No products found.</p>
              <p className="mt-2 text-[var(--muted-fg)]">Try a different category or search term.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--muted-fg)] mb-6" data-testid="results-count">
                {sorted.length} {sorted.length === 1 ? "product" : "products"}
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7">
                {sorted.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
